import { Injectable } from '@angular/core';
import { ApiService } from '../provider/api.service';
import { endpoints } from '../constant/endpoints';
import { BehaviorSubject, Observable, forkJoin } from 'rxjs';
import { first, map } from 'rxjs/operators';

export interface CustomerPreferences {
  allergyIds: Set<number>;
  dislikeIds: Set<number>;
  loaded: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CustomerPreferencesService {
  private preferences$ = new BehaviorSubject<CustomerPreferences>({
    allergyIds: new Set(),
    dislikeIds: new Set(),
    loaded: false
  });

  private loadingPromise: Promise<CustomerPreferences> | null = null;

  constructor(private apiService: ApiService) {}

  getPreferences(): CustomerPreferences {
    return this.preferences$.getValue();
  }

  observePreferences(): Observable<CustomerPreferences> {
    return this.preferences$.asObservable();
  }

  async loadPreferences(customerId: number): Promise<CustomerPreferences> {
    if (!customerId) {
      console.warn('[CustomerPreferencesService] No customerId provided');
      return this.getPreferences();
    }

    const current = this.getPreferences();
    if (current.loaded) {
      return current;
    }

    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loadingPromise = this.fetchPreferences(customerId);
    const result = await this.loadingPromise;
    this.loadingPromise = null;
    return result;
  }

  private async fetchPreferences(customerId: number): Promise<CustomerPreferences> {
    try {
      const allergiesUrl = `${endpoints.allergies}?customer_id=${customerId}`;
      const dislikesUrl = `${endpoints.dislikes}?customer_id=${customerId}`;

      const [allergiesResponse, dislikesResponse] = await Promise.all([
        this.apiService.get(allergiesUrl).pipe(first()).toPromise(),
        this.apiService.get(dislikesUrl).pipe(first()).toPromise()
      ]);

      const allergyIds = new Set<number>(
        (allergiesResponse as any)?.data?.customer_selected_allergies || []
      );
      const dislikeIds = new Set<number>(
        (dislikesResponse as any)?.data?.customer_selected_dislikes || []
      );

      const preferences: CustomerPreferences = {
        allergyIds,
        dislikeIds,
        loaded: true
      };

      this.preferences$.next(preferences);
      console.log('[CustomerPreferencesService] Loaded preferences:', {
        allergyCount: allergyIds.size,
        dislikeCount: dislikeIds.size
      });

      return preferences;
    } catch (error) {
      console.error('[CustomerPreferencesService] Error loading preferences:', error);
      return this.getPreferences();
    }
  }

  async refreshPreferences(customerId: number): Promise<CustomerPreferences> {
    this.preferences$.next({
      allergyIds: new Set(),
      dislikeIds: new Set(),
      loaded: false
    });
    this.loadingPromise = null;
    return this.loadPreferences(customerId);
  }

  clearPreferences(): void {
    this.preferences$.next({
      allergyIds: new Set(),
      dislikeIds: new Set(),
      loaded: false
    });
    this.loadingPromise = null;
  }

  checkRecipeConflict(recipeId: number, isMandatory: boolean): number {
    const prefs = this.getPreferences();
    
    if (prefs.dislikeIds.has(recipeId)) {
      return isMandatory ? 20 : 2;
    }
    if (prefs.allergyIds.has(recipeId)) {
      return isMandatory ? 10 : 1;
    }
    return 0;
  }

checkMenuItemHealthCode(recipes: any[]): number {
  let maxConflictCode = 0;

  const processItems = (items: any[]) => {
    if (!Array.isArray(items)) return;

    for (const item of items) {
      const isMandatory = String(item.is_mandatory_for_menu_item) === '1';
      const conflictCode = this.checkRecipeConflict(Number(item.id), isMandatory);

      if (conflictCode > maxConflictCode) {
        maxConflictCode = conflictCode;
      }

      // Handle the nested structure: item.recipes might be an object containing an array
      if (item.recipes) {
        const nestedList = Array.isArray(item.recipes) 
          ? item.recipes 
          : (item.recipes.recipes || []); // Handles the "recipes: { recipes: [...] }" pattern
        
        processItems(nestedList);
      }
    }
  };

  processItems(recipes);
  return maxConflictCode;
}
}
